// src/contexts/theme/ThemeContext.js
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { themes } from './themes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : true; // Default to dark mode
  });

  const [isAlexMode, setIsAlexMode] = useState(() => {
    const saved = localStorage.getItem('alexMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Use useMemo to compute the current theme
  const currentTheme = useMemo(() => {
    if (isAlexMode) {
      return isDarkMode ? themes.alexModeDark : themes.alexModeLight;
    }
    return isDarkMode ? themes.dark : themes.light;
  }, [isDarkMode, isAlexMode]);

  // Apply theme to document element for CSS variable switching
  useEffect(() => {
    // Remove old body theme classes (legacy cleanup)
    document.body.classList.remove(
      'bg-white',
      'bg-gray-900',
      'text-gray-900',
      'text-white'
    );

    // Add/remove dark class on HTML element for Tailwind
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.classList.add('light');
    }
  }, [isDarkMode]);

  // Apply Alex mode class for colorblind-safe CSS variable overrides
  useEffect(() => {
    if (isAlexMode) {
      document.documentElement.classList.add('alex-mode');
    } else {
      document.documentElement.classList.remove('alex-mode');
    }
  }, [isAlexMode]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    localStorage.setItem('alexMode', JSON.stringify(isAlexMode));
  }, [isDarkMode, isAlexMode]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);
  const toggleAlexMode = () => setIsAlexMode(prev => !prev);

  const value = useMemo(() => ({
    theme: currentTheme,
    isDarkMode,
    isAlexMode,
    toggleDarkMode,
    toggleAlexMode
  }), [currentTheme, isDarkMode, isAlexMode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;